import React, { useState, useEffect } from 'react';
import {
  getDeployableVersions,
  addMinorVersion,
  deleteMinorVersion,
  addPatchVersion,
  updatePatchVersion,
  deletePatchVersion,
  updateMinorVersionNotes
} from '../utils/apiService';

// Configuration des statuts
const STATUS_CONFIG = {
  authorized: {
    label: 'Autorisee',
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ),
    description: 'Vous pouvez installer cette version chez le client'
  },
  authorized_with_reservations: {
    label: 'Autorisee avec reserves',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    description: 'Installation possible, consultez le rapport pour voir les reserves'
  },
  pending_authorization: {
    label: 'En cours d\'autorisation',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: (
      <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
    description: 'Le rapport de verification est en cours de redaction'
  },
  pending_authorization_with_reservations: {
    label: 'En cours d\'autorisation avec reserves',
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    icon: (
      <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
    description: 'Le rapport avec reserves est en cours de redaction'
  },
  not_authorized: {
    label: 'Non autorisee',
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    ),
    description: 'Vous ne pouvez pas installer cette version chez le client'
  }
};

// Configuration des composants de deploiement
const DEPLOYMENT_COMPONENTS = {
  marshaller: { label: 'Marshaller', color: 'bg-blue-100 text-blue-800 border-blue-300', dotColor: 'bg-blue-500', iconColor: 'text-blue-600' },
  patientApp: { label: 'Patient App', color: 'bg-green-100 text-green-800 border-green-300', dotColor: 'bg-green-500', iconColor: 'text-green-600' },
  managerFrontApp: { label: 'Manager Front App', color: 'bg-purple-100 text-purple-800 border-purple-300', dotColor: 'bg-purple-500', iconColor: 'text-purple-600' },
  marshallerInterop: { label: 'Marshaller Interop', color: 'bg-orange-100 text-orange-800 border-orange-300', dotColor: 'bg-orange-500', iconColor: 'text-orange-600' }
};

const EMPTY_DEPLOYMENT_NOTES = {
  marshaller: '',
  patientApp: '',
  managerFrontApp: '',
  marshallerInterop: ''
};

// Composant carte pour afficher une note de deploiement par composant
const ComponentNoteCard = ({ component, note }) => {
  const config = DEPLOYMENT_COMPONENTS[component];
  if (!config || !note) return null;
  return (
    <div className={`p-3 rounded-lg border ${config.color}`}>
      <div className="flex items-center mb-1">
        <span className={`inline-block w-2 h-2 rounded-full ${config.dotColor} mr-2`} />
        <span className="font-semibold text-sm">{config.label}</span>
      </div>
      <pre className="text-sm whitespace-pre-wrap font-mono bg-white bg-opacity-50 p-2 rounded mt-1">
        {note}
      </pre>
    </div>
  );
};

const DeployableVersions = () => {
  const [data, setData] = useState({ minorVersions: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  const [showAddMinorModal, setShowAddMinorModal] = useState(false);
  const [showAddPatchModal, setShowAddPatchModal] = useState(false);
  const [showEditPatchModal, setShowEditPatchModal] = useState(false);
  const [editingPatch, setEditingPatch] = useState(null);
  const [newMinorVersion, setNewMinorVersion] = useState({ name: '', description: '' });
  const [newPatch, setNewPatch] = useState({
    version: '',
    status: 'not_authorized',
    expirationDate: '',
    reportLink: '',
    notes: '',
    deploymentNotes: { ...EMPTY_DEPLOYMENT_NOTES }
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState({});
  const [showMinorNotesModal, setShowMinorNotesModal] = useState(false);
  const [editingMinorNotes, setEditingMinorNotes] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Definir le premier onglet comme actif par defaut
    if (data.minorVersions.length > 0 && !activeTab) {
      setActiveTab(data.minorVersions[0].id);
    }
  }, [data, activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await getDeployableVersions();
      setData(result);
    } catch (err) {
      setError('Erreur lors du chargement des donnees');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMinorVersion = async () => {
    if (!newMinorVersion.name) {
      setError('Le nom de la version est requis');
      return;
    }
    try {
      await addMinorVersion(newMinorVersion);
      setSuccess('Version mineure ajoutee avec succes');
      setShowAddMinorModal(false);
      setNewMinorVersion({ name: '', description: '' });
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteMinorVersion = async (minorId) => {
    if (!window.confirm('Etes-vous sur de vouloir supprimer cette version mineure et tous ses patches ?')) {
      return;
    }
    try {
      await deleteMinorVersion(minorId);
      setSuccess('Version mineure supprimee');
      if (activeTab === minorId) {
        setActiveTab(data.minorVersions[0]?.id || null);
      }
      loadData();
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
  };

  const handleAddPatch = async () => {
    if (!newPatch.version) {
      setError('Le numero de version est requis');
      return;
    }
    try {
      await addPatchVersion(activeTab, newPatch);
      setSuccess('Version de patch ajoutee avec succes');
      setShowAddPatchModal(false);
      setNewPatch({
        version: '',
        status: 'not_authorized',
        expirationDate: '',
        reportLink: '',
        notes: '',
        deploymentNotes: { ...EMPTY_DEPLOYMENT_NOTES }
      });
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditPatch = (patch) => {
    setEditingPatch({
      ...patch,
      expirationDate: patch.expirationDate || '',
      deploymentNotes: patch.deploymentNotes || { ...EMPTY_DEPLOYMENT_NOTES }
    });
    setShowEditPatchModal(true);
  };

  const handleUpdatePatch = async () => {
    try {
      await updatePatchVersion(activeTab, editingPatch.id, editingPatch);
      setSuccess('Version de patch mise a jour');
      setShowEditPatchModal(false);
      setEditingPatch(null);
      loadData();
    } catch (err) {
      setError('Erreur lors de la mise a jour');
    }
  };

  const handleDeletePatch = async (patchId) => {
    if (!window.confirm('Etes-vous sur de vouloir supprimer cette version de patch ?')) {
      return;
    }
    try {
      await deletePatchVersion(activeTab, patchId);
      setSuccess('Version de patch supprimee');
      loadData();
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
  };

  const getActiveMinorVersion = () => {
    return data.minorVersions.find(v => v.id === activeTab);
  };

  const getFilteredPatches = () => {
    const minorVersion = getActiveMinorVersion();
    if (!minorVersion) return [];

    return minorVersion.patches.filter(patch => {
      const matchesSearch = patch.version.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || patch.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR');
  };

  const isExpiringSoon = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    return diffDays > 0 && diffDays <= 30;
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    return date < new Date();
  };

  // Helpers pour les notes de deploiement
  const hasAnyDeploymentNotes = (patch) => {
    const notes = patch.deploymentNotes;
    if (!notes) return false;
    return Object.values(notes).some(v => v && v.trim());
  };

  const countDeploymentNotes = (patch) => {
    const notes = patch.deploymentNotes;
    if (!notes) return 0;
    return Object.values(notes).filter(v => v && v.trim()).length;
  };

  const hasMinorLevelNotes = (minorVersion) => {
    if (!minorVersion || !minorVersion.deploymentNotes) return false;
    return Object.values(minorVersion.deploymentNotes).some(v => v && v.trim());
  };

  const toggleExpandedNotes = (patchId) => {
    setExpandedNotes(prev => ({ ...prev, [patchId]: !prev[patchId] }));
  };

  // Gestion des notes de version mineure
  const handleOpenMinorNotes = () => {
    const minor = getActiveMinorVersion();
    if (!minor) return;
    setEditingMinorNotes({
      id: minor.id,
      name: minor.name,
      deploymentNotes: minor.deploymentNotes || { ...EMPTY_DEPLOYMENT_NOTES }
    });
    setShowMinorNotesModal(true);
  };

  const handleSaveMinorNotes = async () => {
    try {
      await updateMinorVersionNotes(editingMinorNotes.id, editingMinorNotes.deploymentNotes);
      setSuccess('Notes de la version mineure mises a jour');
      setShowMinorNotesModal(false);
      setEditingMinorNotes(null);
      loadData();
    } catch (err) {
      setError('Erreur lors de la sauvegarde des notes');
    }
  };

  // Message d'erreur/succes
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Versions Deployables</h1>
          <p className="text-gray-600 mt-1">
            Gerez les versions autorisees pour le deploiement chez les clients
          </p>
        </div>
        <button
          onClick={() => setShowAddMinorModal(true)}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Ajouter une version mineure
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {success}
        </div>
      )}

      {/* Legende des statuts */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="font-semibold text-gray-700 mb-3">Legende des statuts</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
            <div key={key} className="flex items-start space-x-2">
              <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${config.color}`}>
                {config.icon}
                <span className="ml-1">{config.label}</span>
              </span>
              <span className="text-xs text-gray-500">{config.description}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Onglets des versions mineures */}
      {data.minorVersions.length > 0 ? (
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px overflow-x-auto">
              {data.minorVersions.map((minor) => (
                <button
                  key={minor.id}
                  onClick={() => setActiveTab(minor.id)}
                  className={`group relative flex-shrink-0 whitespace-nowrap px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === minor.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    Version {minor.name}
                    <span className="ml-2 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                      {minor.patches.length}
                    </span>
                  </span>
                  {/* Bouton supprimer */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteMinorVersion(minor.id);
                    }}
                    className="absolute top-1 right-1 p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Supprimer cette version mineure"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </button>
              ))}
            </nav>
          </div>

          {/* Contenu de l'onglet */}
          {activeTab && (
            <div className="p-6">
              {/* Barre d'outils */}
              <div className="flex flex-wrap gap-4 mb-6">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Rechercher une version..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <svg className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">Tous les statuts</option>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
                <button
                  onClick={handleOpenMinorNotes}
                  className="inline-flex items-center px-3 py-2 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 border border-amber-300 transition-colors"
                  title={`Notes de deploiement pour toute la version ${activeTab}.x`}
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Notes v{activeTab}.x
                  {hasMinorLevelNotes(getActiveMinorVersion()) && (
                    <span className="ml-1.5 w-2 h-2 bg-amber-500 rounded-full inline-block" />
                  )}
                </button>
                <button
                  onClick={() => setShowAddPatchModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Ajouter un patch
                </button>
              </div>

              {/* Tableau des patches */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Version
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date de fin de validite
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rapport
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Notes de deploiement
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getFilteredPatches().map((patch) => (
                      <React.Fragment key={patch.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-medium text-gray-900">{patch.version}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${STATUS_CONFIG[patch.status]?.color || 'bg-gray-100 text-gray-800'}`}>
                            {STATUS_CONFIG[patch.status]?.icon}
                            <span className="ml-1">{STATUS_CONFIG[patch.status]?.label || patch.status}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {patch.expirationDate ? (
                            <span className={`inline-flex items-center ${
                              isExpired(patch.expirationDate)
                                ? 'text-red-600'
                                : isExpiringSoon(patch.expirationDate)
                                  ? 'text-orange-600'
                                  : 'text-gray-600'
                            }`}>
                              {isExpired(patch.expirationDate) && (
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                              )}
                              {formatDate(patch.expirationDate)}
                              {isExpired(patch.expirationDate) && <span className="ml-1 text-xs">(Expiree)</span>}
                              {isExpiringSoon(patch.expirationDate) && !isExpired(patch.expirationDate) && <span className="ml-1 text-xs">(Bientot)</span>}
                            </span>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {patch.reportLink ? (
                            <a
                              href={patch.reportLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors group"
                              title={`Ouvrir: ${patch.reportLink}`}
                            >
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span className="text-sm truncate max-w-[150px]">
                                {patch.reportLink.split('/').pop() || patch.reportLink}
                              </span>
                              <svg className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          ) : (
                            <span className="text-gray-400 text-sm italic">Aucun rapport</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {hasAnyDeploymentNotes(patch) || (patch.notes && patch.notes.trim()) ? (
                            <button
                              onClick={() => toggleExpandedNotes(patch.id)}
                              className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800"
                            >
                              <span className="flex items-center space-x-0.5 mr-1">
                                {Object.entries(patch.deploymentNotes || {}).filter(([_, v]) => v && v.trim()).map(([key]) => (
                                  <span key={key} className={`inline-block w-2 h-2 rounded-full ${DEPLOYMENT_COMPONENTS[key]?.dotColor}`} title={DEPLOYMENT_COMPONENTS[key]?.label} />
                                ))}
                              </span>
                              <span>{countDeploymentNotes(patch)} note(s)</span>
                              <svg className={`w-4 h-4 transition-transform ${expandedNotes[patch.id] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            onClick={() => handleEditPatch(patch)}
                            className="text-blue-600 hover:text-blue-800 mr-3"
                            title="Modifier"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeletePatch(patch.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Supprimer"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                      {/* Ligne expandable des notes de deploiement */}
                      {expandedNotes[patch.id] && (
                        <tr>
                          <td colSpan="6" className="px-6 py-4 bg-gray-50 border-b">
                            {/* Notes generales du patch */}
                            {patch.notes && patch.notes.trim() && (
                              <div className="mb-3 p-3 bg-white rounded-lg border border-gray-200">
                                <h5 className="text-xs font-semibold text-gray-500 uppercase mb-1">Notes generales</h5>
                                <pre className="text-sm whitespace-pre-wrap font-mono text-gray-700">{patch.notes}</pre>
                              </div>
                            )}
                            {/* Notes heritees de la version mineure */}
                            {hasMinorLevelNotes(getActiveMinorVersion()) && (
                              <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <h5 className="text-xs font-semibold text-amber-700 uppercase mb-2 flex items-center">
                                  <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Notes pour toute la version {getActiveMinorVersion().name}.x
                                </h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                  {Object.entries(getActiveMinorVersion().deploymentNotes || {})
                                    .filter(([_, note]) => note && note.trim())
                                    .map(([component, note]) => (
                                      <ComponentNoteCard key={component} component={component} note={note} />
                                    ))}
                                </div>
                              </div>
                            )}
                            {/* Notes specifiques au patch */}
                            {hasAnyDeploymentNotes(patch) && (
                              <div>
                                <h5 className="text-xs font-semibold text-gray-500 uppercase mb-2">Notes specifiques a la version {patch.version}</h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {Object.entries(patch.deploymentNotes || {})
                                    .filter(([_, note]) => note && note.trim())
                                    .map(([component, note]) => (
                                      <ComponentNoteCard key={component} component={component} note={note} />
                                    ))}
                                </div>
                              </div>
                            )}
                            {!hasAnyDeploymentNotes(patch) && !hasMinorLevelNotes(getActiveMinorVersion()) && !(patch.notes && patch.notes.trim()) && (
                              <p className="text-sm text-gray-400 italic">Aucune note de deploiement</p>
                            )}
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    ))}
                    {getFilteredPatches().length === 0 && (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                          <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                          </svg>
                          Aucune version de patch trouvee
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Statistiques */}
              <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
                {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                  const count = getActiveMinorVersion()?.patches.filter(p => p.status === key).length || 0;
                  return (
                    <div key={key} className={`p-4 rounded-lg border ${config.color.replace('text-', 'border-').split(' ')[0]}`}>
                      <div className="text-2xl font-bold">{count}</div>
                      <div className="text-sm">{config.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune version mineure</h3>
          <p className="text-gray-500 mb-4">Commencez par ajouter une version mineure pour gerer ses patches.</p>
          <button
            onClick={() => setShowAddMinorModal(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Ajouter une version mineure
          </button>
        </div>
      )}

      {/* Modal: Ajouter version mineure */}
      {showAddMinorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Ajouter une version mineure</h3>
              <button onClick={() => setShowAddMinorModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de la version *
                </label>
                <input
                  type="text"
                  placeholder="Ex: 7.45"
                  value={newMinorVersion.name}
                  onChange={(e) => setNewMinorVersion({ ...newMinorVersion, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  placeholder="Description optionnelle"
                  value={newMinorVersion.description}
                  onChange={(e) => setNewMinorVersion({ ...newMinorVersion, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="3"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowAddMinorModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Annuler
              </button>
              <button
                onClick={handleAddMinorVersion}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Ajouter patch */}
      {showAddPatchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Ajouter une version de patch</h3>
              <button onClick={() => setShowAddPatchModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Numero de version *
                </label>
                <input
                  type="text"
                  placeholder={`Ex: ${activeTab}.0`}
                  value={newPatch.version}
                  onChange={(e) => setNewPatch({ ...newPatch, version: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Statut
                </label>
                <select
                  value={newPatch.status}
                  onChange={(e) => setNewPatch({ ...newPatch, status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date de fin de validite
                </label>
                <input
                  type="date"
                  value={newPatch.expirationDate}
                  onChange={(e) => setNewPatch({ ...newPatch, expirationDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lien vers le rapport
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  </div>
                  <input
                    type="url"
                    placeholder="https://sharepoint.com/... ou lien vers le fichier"
                    value={newPatch.reportLink}
                    onChange={(e) => setNewPatch({ ...newPatch, reportLink: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Collez l'URL complete du rapport (SharePoint, Google Drive, etc.)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes generales
                </label>
                <textarea
                  placeholder="Notes additionnelles..."
                  value={newPatch.notes}
                  onChange={(e) => setNewPatch({ ...newPatch, notes: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="2"
                />
              </div>
              {/* Notes de deploiement par composant */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Notes de deploiement par composant
                </h4>
                <div className="space-y-3">
                  {Object.entries(DEPLOYMENT_COMPONENTS).map(([key, config]) => (
                    <div key={key}>
                      <label className={`block text-sm font-medium mb-1 ${config.iconColor}`}>
                        <span className={`inline-block w-2 h-2 rounded-full ${config.dotColor} mr-1.5`} />
                        {config.label}
                      </label>
                      <textarea
                        placeholder={`Instructions de deploiement pour ${config.label}...`}
                        value={newPatch.deploymentNotes[key]}
                        onChange={(e) => setNewPatch({
                          ...newPatch,
                          deploymentNotes: { ...newPatch.deploymentNotes, [key]: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                        rows="3"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowAddPatchModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Annuler
              </button>
              <button
                onClick={handleAddPatch}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Editer patch */}
      {showEditPatchModal && editingPatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Modifier la version {editingPatch.version}</h3>
              <button onClick={() => setShowEditPatchModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Statut
                </label>
                <select
                  value={editingPatch.status}
                  onChange={(e) => setEditingPatch({ ...editingPatch, status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date de fin de validite
                </label>
                <input
                  type="date"
                  value={editingPatch.expirationDate || ''}
                  onChange={(e) => setEditingPatch({ ...editingPatch, expirationDate: e.target.value || null })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lien vers le rapport
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  </div>
                  <input
                    type="url"
                    placeholder="https://sharepoint.com/... ou lien vers le fichier"
                    value={editingPatch.reportLink}
                    onChange={(e) => setEditingPatch({ ...editingPatch, reportLink: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Collez l'URL complete du rapport (SharePoint, Google Drive, etc.)</p>
                {editingPatch.reportLink && (
                  <a
                    href={editingPatch.reportLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    Tester le lien
                  </a>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes generales
                </label>
                <textarea
                  placeholder="Notes additionnelles..."
                  value={editingPatch.notes}
                  onChange={(e) => setEditingPatch({ ...editingPatch, notes: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="2"
                />
              </div>
              {/* Notes de deploiement par composant */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Notes de deploiement par composant
                </h4>
                <div className="space-y-3">
                  {Object.entries(DEPLOYMENT_COMPONENTS).map(([key, config]) => (
                    <div key={key}>
                      <label className={`block text-sm font-medium mb-1 ${config.iconColor}`}>
                        <span className={`inline-block w-2 h-2 rounded-full ${config.dotColor} mr-1.5`} />
                        {config.label}
                      </label>
                      <textarea
                        placeholder={`Instructions de deploiement pour ${config.label}...`}
                        value={editingPatch.deploymentNotes[key]}
                        onChange={(e) => setEditingPatch({
                          ...editingPatch,
                          deploymentNotes: { ...editingPatch.deploymentNotes, [key]: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                        rows="3"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowEditPatchModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Annuler
              </button>
              <button
                onClick={handleUpdatePatch}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Notes de deploiement version mineure */}
      {showMinorNotesModal && editingMinorNotes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b bg-amber-50">
              <div>
                <h3 className="text-lg font-semibold text-amber-900">Notes de deploiement - Version {editingMinorNotes.name}.x</h3>
                <p className="text-sm text-amber-700 mt-0.5">Ces notes s'appliqueront a tous les patches de la version {editingMinorNotes.name}</p>
              </div>
              <button onClick={() => setShowMinorNotesModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {Object.entries(DEPLOYMENT_COMPONENTS).map(([key, config]) => (
                <div key={key}>
                  <label className={`block text-sm font-medium mb-1 ${config.iconColor}`}>
                    <span className={`inline-block w-2 h-2 rounded-full ${config.dotColor} mr-1.5`} />
                    {config.label}
                  </label>
                  <textarea
                    placeholder={`Instructions de deploiement pour ${config.label} (applicables a toute la ${editingMinorNotes.name}.x)...`}
                    value={editingMinorNotes.deploymentNotes[key]}
                    onChange={(e) => setEditingMinorNotes({
                      ...editingMinorNotes,
                      deploymentNotes: { ...editingMinorNotes.deploymentNotes, [key]: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 font-mono text-sm"
                    rows="4"
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-end space-x-3 p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowMinorNotesModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveMinorNotes}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeployableVersions;
